import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, Pencil, Printer, Save, Send, UtensilsCrossed } from 'lucide-react';
import {
  fetchReservationsBureau,
  modifierReservationRepas,
  renvoyerConfirmationRepas,
  updateEvenement,
} from '../services/api';
import { nomFamille, prenomUsage } from '../utils/nom';
import { imprimerListe, type ColonneImpression } from '../utils/impression';

import { dateLongueHeureFr } from '../utils/dates';
const euros = (montant: number) =>
  new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 })
    .format(montant);

const statutLabels: Record<string, { label: string; classe: string }> = {
  payee: { label: 'Payée', classe: 'bg-green-50 text-green-700' },
  en_attente: { label: 'En attente', classe: 'bg-amber-50 text-amber-700' },
  annulee: { label: 'Annulée', classe: 'bg-gray-100 text-gray-500 dark:text-gray-400 dark:bg-gray-700' },
};

const dateLisible = (iso?: string | null) =>
  iso ? dateLongueHeureFr(iso) : '';

/**
 * Colonnes de la feuille de service.
 *
 * Le code de retrait accompagne la référence : c'est lui que le convive
 * présente. Les observations restent en clair — une allergie lue en cuisine
 * vaut mieux qu'une allergie découverte à table.
 */
const colonnesImpression: ColonneImpression<any>[] = [
  {
    titre: 'Référence',
    valeur: (r) => [r.reference, r.codeRetrait].filter(Boolean).join('\n'),
  },
  { titre: 'Convive', valeur: (r) => [r.prenom, r.nom].filter(Boolean).join(' ') },
  { titre: 'Téléphone', valeur: (r) => r.telephone ?? '' },
  { titre: 'Ad.', valeur: (r) => String(r.nbAdultes ?? 0), alignement: 'centre' },
  { titre: 'Enf.', valeur: (r) => String(r.nbEnfants ?? 0), alignement: 'centre' },
  { titre: 'Montant', valeur: (r) => euros(r.montantTotal ?? 0), alignement: 'droite' },
  { titre: 'Statut', valeur: (r) => statutLabels[r.statut]?.label ?? r.statut ?? '' },
  { titre: 'Observations', valeur: (r) => r.commentaire ?? '' },
];

/** Les champs date attendent le format accepté par datetime-local. */
const pourChamp = (iso?: string | null) => (iso ? iso.slice(0, 16) : '');

/**
 * Repas d'un événement : tarifs, quotas et liste des réservations.
 *
 * Les quotas sont la seule chose qui ferme le bouton public : une fois les
 * couverts vendus, le visiteur voit « Complet » sans intervention du bureau.
 */
export default function OngletRepas({
  evenementId,
  evenement,
  onSucces,
  onEchec,
}: {
  evenementId: number;
  evenement: any;
  onSucces: (message: string) => void;
  onEchec: (error: Error) => void;
}) {
  const queryClient = useQueryClient();

  const [form, setForm] = useState({
    repasActif: false,
    repasDescription: '',
    repasPrixAdulte: 0,
    repasPrixEnfant: 0,
    repasQuotaAdulte: 0,
    repasQuotaEnfant: 0,
    repasDate: '',
    repasDateLimite: '',
  });

  // Le formulaire porte l'état enregistré dès qu'il est connu : partir de
  // zéro remettrait les quotas à néant au premier enregistrement.
  useEffect(() => {
    if (!evenement) return;
    setForm({
      repasActif: evenement.repasActif === true,
      repasDescription: evenement.repasDescription ?? '',
      repasPrixAdulte: evenement.repasPrixAdulte ?? 0,
      repasPrixEnfant: evenement.repasPrixEnfant ?? 0,
      repasQuotaAdulte: evenement.repasQuotaAdulte ?? 0,
      repasQuotaEnfant: evenement.repasQuotaEnfant ?? 0,
      // Aucun repli sur la date de l'événement : proposer celle-ci laissait
      // croire que les deux sont liées, alors que le repas se sert quand on
      // veut — la veille pour un montage, le lendemain midi, ou un autre soir.
      repasDate: pourChamp(evenement.repasDate),
      repasDateLimite: pourChamp(evenement.repasDateLimite),
    });
  }, [evenement]);

  const query = useQuery({
    queryKey: ['evenement-repas', evenementId],
    queryFn: () => fetchReservationsBureau(evenementId),
  });

  const enregistrer = useMutation({
    mutationFn: () =>
      updateEvenement(evenementId, {
        ...form,
        repasDate: form.repasDate || null,
        repasDateLimite: form.repasDateLimite || null,
        repasDescription: form.repasDescription.trim() || null,
      }),
    onSuccess: () => {
      onSucces('Repas mis à jour.');
      queryClient.invalidateQueries({ queryKey: ['evenement-repas', evenementId] });
      queryClient.invalidateQueries({ queryKey: ['bureau-evenements'] });
    },
    onError: onEchec,
  });

  const etat = query.data?.repas;
  const totaux = query.data?.totaux;
  const reservations: any[] = query.data?.reservations ?? [];

  return (
    <div className="space-y-5">
      <section className="card lg:p-5 space-y-4">
        <label className="flex items-start gap-2 text-sm">
          <input
            type="checkbox"
            checked={form.repasActif}
            onChange={(e) => setForm({ ...form, repasActif: e.target.checked })}
            className="mt-0.5"
          />
          <span>
            <span className="font-medium">Proposer un repas sur réservation</span>
            <span className="block text-xs text-gray-500 dark:text-gray-400">
              Le bloc de réservation n'apparaît sur la page publique que si l'événement est publié.
            </span>
          </span>
        </label>

        {form.repasActif && (
          <>
            <textarea
              className="input min-h-[4rem]"
              placeholder="Menu, horaire du service, précisions…"
              value={form.repasDescription}
              onChange={(e) => setForm({ ...form, repasDescription: e.target.value })}
            />

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              <label className="text-sm text-gray-600 dark:text-gray-400">
                Prix adulte (€)
                <input
                  className="input mt-1"
                  type="number"
                  min={0}
                  value={form.repasPrixAdulte}
                  onChange={(e) => setForm({ ...form, repasPrixAdulte: Number(e.target.value) })}
                />
              </label>
              <label className="text-sm text-gray-600 dark:text-gray-400">
                Prix enfant (€)
                <input
                  className="input mt-1"
                  type="number"
                  min={0}
                  value={form.repasPrixEnfant}
                  onChange={(e) => setForm({ ...form, repasPrixEnfant: Number(e.target.value) })}
                />
              </label>
              <label className="text-sm text-gray-600 dark:text-gray-400">
                Couverts adultes
                <input
                  className="input mt-1"
                  type="number"
                  min={0}
                  value={form.repasQuotaAdulte}
                  onChange={(e) => setForm({ ...form, repasQuotaAdulte: Number(e.target.value) })}
                />
              </label>
              <label className="text-sm text-gray-600 dark:text-gray-400">
                Couverts enfants
                <input
                  className="input mt-1"
                  type="number"
                  min={0}
                  value={form.repasQuotaEnfant}
                  onChange={(e) => setForm({ ...form, repasQuotaEnfant: Number(e.target.value) })}
                />
              </label>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
              <label className="text-sm text-gray-600 block dark:text-gray-400">
                Date et heure du repas <span className="text-red-600">*</span>
                <input
                  className="input mt-1"
                  type="datetime-local"
                  value={form.repasDate}
                  onChange={(e) => setForm({ ...form, repasDate: e.target.value })}
                />
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  Libre, sans lien avec les dates de l'événement : la veille pour le montage,
                  un autre soir, le lendemain midi. C'est cette heure que le convive lit sur
                  sa confirmation et à laquelle il se présente.
                </span>
              </label>

              <label className="text-sm text-gray-600 block dark:text-gray-400">
                Clôture des réservations (facultatif)
                <input
                  className="input mt-1"
                  type="datetime-local"
                  value={form.repasDateLimite}
                  max={form.repasDate || undefined}
                  onChange={(e) => setForm({ ...form, repasDateLimite: e.target.value })}
                />
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  Passée cette date, le bouton se ferme même s'il reste des couverts.
                </span>
              </label>
            </div>
          </>
        )}

        <button
          onClick={() => enregistrer.mutate()}
          disabled={enregistrer.isPending}
          className="btn-primary inline-flex items-center gap-2 disabled:opacity-50"
        >
          {enregistrer.isPending ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Save className="w-4 h-4" />
          )}
          Enregistrer
        </button>
      </section>

      {/* Suivi des ventes */}
      {etat?.actif && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <Indicateur libelle="Réservations" valeur={String(totaux?.reservations ?? 0)} />
            <Indicateur libelle="Couverts vendus" valeur={String(totaux?.couverts ?? 0)} />
            {/* Le total seul ne dit pas si le service est encore ouvert : il
                reste souvent des parts enfant quand les adultes sont épuisés,
                et c'est l'épuisement des adultes qui affiche « Complet ». */}
            <Indicateur
              libelle="Couverts restants"
              valeur={String(etat.restantTotal)}
              badges={[
                {
                  texte: `${etat.restantAdulte} adulte${etat.restantAdulte > 1 ? 's' : ''}`,
                  classe:
                    'bg-sky-100 text-sky-800 dark:bg-sky-900/40 dark:text-sky-200',
                },
                {
                  texte: `${etat.restantEnfant} enfant${etat.restantEnfant > 1 ? 's' : ''}`,
                  classe:
                    'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200',
                },
              ]}
              accent={etat.complet ? 'text-red-600' : 'text-gray-900 dark:text-gray-100'}
            />
            <Indicateur
              libelle="Encaissé"
              valeur={euros(totaux?.encaisse ?? 0)}
              accent="text-green-600"
            />
          </div>

          {etat.complet && (
            <p className="text-sm rounded-xl px-3 py-2 bg-red-50 text-red-700">
              Le repas affiche « Complet » sur la page publique. Augmentez les quotas pour
              rouvrir les réservations.
            </p>
          )}

          {/* Le soir du service, la liste se pointe au stylo : la feuille
              porte une colonne « Servi » à cocher. */}
          <div className="flex items-center justify-between gap-3">
            <h3 className="font-semibold text-gray-900 dark:text-gray-100">
              Réservations
            </h3>
            <button
              type="button"
              onClick={() =>
                imprimerListe({
                  titre: 'Repas — liste des réservations',
                  sousTitre: [evenement?.nom, dateLisible(evenement?.repasDate)]
                    .filter(Boolean)
                    .join(' — '),
                  totaux: [
                    { libelle: 'réservations', valeur: String(totaux?.reservations ?? 0) },
                    { libelle: 'couverts', valeur: String(totaux?.couverts ?? 0) },
                    { libelle: 'encaissé', valeur: euros(totaux?.encaisse ?? 0) },
                  ],
                  colonnes: colonnesImpression,
                  lignes: reservations,
                  colonnePointage: 'Servi',
                })
              }
              disabled={reservations.length === 0}
              className="btn-outline inline-flex items-center gap-1.5 text-sm px-3 py-1.5 disabled:opacity-50"
            >
              <Printer className="w-4 h-4" />
              Imprimer la liste
            </button>
          </div>

          <section className="card p-0 overflow-x-auto">
            {query.isPending ? (
              <div className="p-6 flex justify-center">
                <Loader2 className="w-5 h-5 animate-spin text-primary-600" />
              </div>
            ) : reservations.length === 0 ? (
              <p className="p-6 text-center text-gray-500 text-sm dark:text-gray-400">Aucune réservation.</p>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-gray-50 dark:bg-gray-800 text-left text-gray-600 dark:text-gray-300">
                  <tr>
                    <th className="px-4 py-3 font-medium">Référence</th>
                    <th className="px-4 py-3 font-medium">Convive</th>
                    <th className="px-4 py-3 font-medium text-center">Ad.</th>
                    <th className="px-4 py-3 font-medium text-center">Enf.</th>
                    <th className="px-4 py-3 font-medium text-right">Montant</th>
                    <th className="px-4 py-3 font-medium">Statut</th>
                    <th className="px-4 py-3 font-medium text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                  {reservations.map((r) => (
                    <LigneReservation
                      key={r.id}
                      reservation={r}
                      onSucces={onSucces}
                      onEchec={onEchec}
                      onModifie={() =>
                        queryClient.invalidateQueries({
                          queryKey: ['evenement-repas', evenementId],
                        })
                      }
                    />
                  ))}
                </tbody>
              </table>
            )}
          </section>
        </>
      )}

      {!etat?.actif && (
        <p className="text-sm text-gray-500 flex items-center gap-2 dark:text-gray-400">
          <UtensilsCrossed className="w-4 h-4 text-gray-400" />
          Activez le repas pour ouvrir les réservations et suivre les ventes.
        </p>
      )}
    </div>
  );
}

/**
 * Une réservation, en lecture ou en correction.
 *
 * Une adresse mal saisie au clavier d'un téléphone est le premier motif de
 * confirmation jamais reçue. Le bureau la reprend ici, puis renvoie le message
 * — sinon le convive se présente à l'accueil sans code.
 *
 * Les quantités et le montant ne sont pas modifiables : ils correspondent à un
 * paiement encaissé chez Stripe.
 */
function LigneReservation({
  reservation: r,
  onSucces,
  onEchec,
  onModifie,
}: {
  reservation: any;
  onSucces: (message: string) => void;
  onEchec: (error: Error) => void;
  onModifie: () => void;
}) {
  const [edition, setEdition] = useState(false);
  const [form, setForm] = useState({
    nom: r.nom ?? '',
    prenom: r.prenom ?? '',
    email: r.email ?? '',
    telephone: r.telephone ?? '',
    commentaire: r.commentaire ?? '',
  });

  const enregistrer = useMutation({
    mutationFn: () =>
      modifierReservationRepas(r.id, {
        nom: form.nom.trim(),
        prenom: form.prenom.trim() || null,
        email: form.email.trim(),
        telephone: form.telephone.trim() || null,
        commentaire: form.commentaire.trim() || null,
      }),
    onSuccess: () => {
      onSucces('Réservation corrigée.');
      setEdition(false);
      onModifie();
    },
    onError: onEchec,
  });

  const renvoyer = useMutation({
    mutationFn: () => renvoyerConfirmationRepas(r.id),
    onSuccess: (d: any) => onSucces(d?.message ?? 'Confirmation renvoyée.'),
    onError: onEchec,
  });

  const statut = statutLabels[r.statut] ?? {
    label: r.statut,
    classe: 'bg-gray-100 text-gray-600 dark:text-gray-400 dark:bg-gray-700',
  };

  if (edition) {
    return (
      <tr className="bg-primary-50/40 dark:bg-primary-900/10">
        <td className="px-4 py-3 font-mono text-xs align-top">{r.reference}</td>
        <td className="px-4 py-3" colSpan={5}>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <input
              className="input"
              placeholder="Nom *"
              value={form.nom}
              onChange={(e) => setForm({ ...form, nom: nomFamille(e.target.value) })}
            />
            <input
              className="input"
              placeholder="Prénom"
              value={form.prenom}
              onChange={(e) => setForm({ ...form, prenom: prenomUsage(e.target.value) })}
            />
            <input
              className="input"
              type="email"
              placeholder="E-mail *"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
            />
            <input
              className="input"
              type="tel"
              placeholder="Téléphone"
              value={form.telephone}
              onChange={(e) => setForm({ ...form, telephone: e.target.value })}
            />
          </div>
          <textarea
            className="input min-h-[3rem] mt-2"
            placeholder="Allergies, régime particulier…"
            value={form.commentaire}
            onChange={(e) => setForm({ ...form, commentaire: e.target.value })}
          />
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
            Les couverts et le montant ne se corrigent pas ici : ils correspondent à un
            paiement encaissé.
          </p>
        </td>
        <td className="px-4 py-3 align-top text-right whitespace-nowrap">
          <button
            onClick={() => enregistrer.mutate()}
            disabled={
              enregistrer.isPending || form.nom.trim() === '' || !form.email.includes('@')
            }
            className="btn-primary px-3 py-1.5 text-sm inline-flex items-center gap-1 disabled:opacity-50"
          >
            {enregistrer.isPending ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
            ) : (
              <Save className="w-3.5 h-3.5" />
            )}
            Enregistrer
          </button>
          <button
            onClick={() => setEdition(false)}
            className="block mt-1 ml-auto text-xs text-gray-500 dark:text-gray-400"
          >
            Annuler
          </button>
        </td>
      </tr>
    );
  }

  return (
    <tr>
      <td className="px-4 py-3 font-mono text-xs">
        {r.reference}
        {r.codeRetrait && (
          <div className="text-[11px] text-gray-400 dark:text-gray-500">{r.codeRetrait}</div>
        )}
      </td>
      <td className="px-4 py-3">
        <div className="font-medium">{[r.prenom, r.nom].filter(Boolean).join(' ')}</div>
        <div className="text-xs text-gray-500 dark:text-gray-400">{r.email}</div>
        {r.telephone && (
          <div className="text-xs text-gray-500 dark:text-gray-400">{r.telephone}</div>
        )}
        {r.commentaire && (
          <div className="text-xs text-amber-700 italic mt-0.5">{r.commentaire}</div>
        )}
      </td>
      <td className="px-4 py-3 text-center tabular-nums">{r.nbAdultes}</td>
      <td className="px-4 py-3 text-center tabular-nums">{r.nbEnfants}</td>
      <td className="px-4 py-3 text-right tabular-nums">{euros(r.montantTotal)}</td>
      <td className="px-4 py-3">
        <span className={`text-xs px-2 py-0.5 rounded-full ${statut.classe}`}>{statut.label}</span>
      </td>
      <td className="px-4 py-3 text-right whitespace-nowrap">
        <button
          onClick={() => setEdition(true)}
          title="Corriger les coordonnées"
          aria-label="Corriger les coordonnées"
          className="p-1.5 rounded-lg text-gray-500 hover:text-primary-700 hover:bg-primary-50 dark:text-gray-400 dark:hover:bg-gray-700"
        >
          <Pencil className="w-4 h-4" />
        </button>
        {/* Le renvoi n'a de sens que sur une réservation payée : c'est elle
            seule qui porte un code de retrait. */}
        {r.statut === 'payee' && (
          <button
            onClick={() => renvoyer.mutate()}
            disabled={renvoyer.isPending}
            title="Renvoyer la confirmation au convive"
            aria-label="Renvoyer la confirmation au convive"
            className="p-1.5 rounded-lg text-gray-500 hover:text-primary-700 hover:bg-primary-50 disabled:opacity-40 dark:text-gray-400 dark:hover:bg-gray-700"
          >
            {renvoyer.isPending ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Send className="w-4 h-4" />
            )}
          </button>
        )}
      </td>
    </tr>
  );
}

/**
 * Un chiffre, et le cas échéant sa décomposition en pastilles.
 *
 * Chaque catégorie a sa couleur : le regard sépare adultes et enfants sans
 * lire. Le total, lui, reste nu — l'encadrer d'une pastille le mettrait sur le
 * même plan que ses parties.
 */
function Indicateur({
  libelle,
  valeur,
  badges,
  accent = 'text-gray-900 dark:text-gray-100',
}: {
  libelle: string;
  valeur: string;
  badges?: { texte: string; classe: string }[];
  accent?: string;
}) {
  return (
    <div className="card py-3 text-center">
      <p className="text-xs text-gray-500 dark:text-gray-400">{libelle}</p>
      <p className={`text-lg lg:text-xl font-bold tabular-nums ${accent}`}>{valeur}</p>
      {badges && badges.length > 0 && (
        <div className="mt-1 flex flex-wrap justify-center gap-1">
          {badges.map((b) => (
            <span
              key={b.texte}
              className={`text-[11px] font-medium px-1.5 py-0.5 rounded-full tabular-nums ${b.classe}`}
            >
              {b.texte}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
